import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, MessageSquare, Send, Sparkles, X } from "lucide-react";
import { scrollToId, trackEvent, waLink } from "@/lib/vgt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What vanilla grades do you offer?",
  "What is your MOQ?",
  "Do you ship to my country?",
  "Can I get samples?",
];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hello! I'm the VGT assistant. Ask me anything about our Indonesian vanilla beans, MOQ, samples, or worldwide shipping.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) listRef.current?.scrollTo(0, listRef.current.scrollHeight);
  }, [messages, open, loading]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    const next = [...messages, { role: "user", content }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { messages: next });
      setMessages([...next, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages([
        ...next,
        {
          role: "assistant",
          content: "Sorry, something went wrong. Please try again or reach our team on WhatsApp.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="ai-chat-button"
        aria-label="Open AI assistant"
        onClick={() => {
          setOpen(!open);
          if (!open) trackEvent("ai_chat_open");
        }}
        className="fixed bottom-5 left-5 z-50 w-14 h-14 rounded-full bg-[#2E4F3E] hover:bg-[#1B3B2B] text-[#FDFBF7] shadow-2xl flex items-center justify-center transition-transform duration-300 hover:scale-105"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>
      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-24 left-4 sm:left-5 z-50 w-[calc(100vw-2rem)] max-w-[380px] bg-[#FDFBF7] border border-[#E6DEC3] rounded-3xl shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="bg-[#2E4F3E] text-[#FDFBF7] px-5 py-4 flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-[#D4A359]/20 text-[#D4A359] flex items-center justify-center shrink-0">
              <Sparkles size={17} />
            </span>
            <div className="leading-tight">
              <p className="font-serif font-bold text-sm">VGT Assistant</p>
              <p className="text-[11px] text-[#FDFBF7]/60">Answers about products, MOQ & shipping</p>
            </div>
          </div>
          <div ref={listRef} data-testid="ai-chat-messages" className="h-72 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#2E4F3E] text-[#FDFBF7] rounded-br-md"
                      : "bg-[#F5EFE6] text-[#2C1A0C] rounded-bl-md"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#F5EFE6] rounded-2xl rounded-bl-md px-4 py-3 flex gap-1.5">
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      className="w-1.5 h-1.5 rounded-full bg-[#6E5B4F]/60 animate-bounce"
                      style={{ animationDelay: `${d * 150}ms` }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          {messages.length === 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={s}
                  data-testid={`ai-chat-chip-${i}`}
                  onClick={() => send(s)}
                  className="text-xs font-medium border border-[#E6DEC3] hover:border-[#2E4F3E] text-[#2C1A0C] rounded-full px-3 py-1.5 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          <div className="px-4 py-2 flex gap-2">
            <button
              data-testid="ai-chat-quote-button"
              onClick={() => {
                setOpen(false);
                scrollToId("quotation");
              }}
              className="flex-1 text-xs font-semibold bg-[#2E4F3E] hover:bg-[#1B3B2B] text-[#FDFBF7] rounded-full px-3 py-2 transition-colors"
            >
              Request a Quotation
            </button>
            <a
              data-testid="ai-chat-whatsapp-link"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click", { location: "ai_chat" })}
              className="flex-1 text-center text-xs font-semibold bg-[#25D366] hover:bg-[#1EAE52] text-white rounded-full px-3 py-2 transition-colors"
            >
              Chat on WhatsApp
            </a>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="p-3 border-t border-[#E6DEC3] flex items-center gap-2"
          >
            <input
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              maxLength={1000}
              placeholder="Ask about vanilla beans…"
              className="flex-1 bg-white border border-[#E6DEC3] rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E4F3E]/40 focus:border-[#2E4F3E]"
            />
            <button
              data-testid="ai-chat-send-button"
              type="submit"
              disabled={loading || !input.trim()}
              className="w-10 h-10 rounded-full bg-[#2E4F3E] hover:bg-[#1B3B2B] disabled:opacity-50 text-[#FDFBF7] flex items-center justify-center transition-colors shrink-0"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};
